import { useLayoutEffect, useRef } from 'react'

import {
  createSnippetReferenceSource,
  type SnippetReferenceSource,
} from '@/components/milkdown/snippet/snippetReferenceSource'
import { useSnippetsQuery } from './useSnippets'

const EMPTY_SNIPPETS = [] as const

/**
 * Keep React Query updates outside the lifetime of the Milkdown editor.
 *
 * `dutyId` scopes the fetch so each snippet also carries that duty's own
 * wording. Without it a block in a duty's script would render the shared body
 * for a snippet the duty has customised — the author sees one thing and the
 * patient hears another.
 */
export function useSnippetReferenceSource(
  enabled: boolean,
  dutyId?: string
): SnippetReferenceSource {
  const { data, isError, isFetching, isPending, refetch } = useSnippetsQuery(
    enabled,
    dutyId
  )
  const sourceRef = useRef<SnippetReferenceSource>(null)
  sourceRef.current ??= createSnippetReferenceSource()
  const source = sourceRef.current

  useLayoutEffect(() => {
    source.update({
      status: !enabled
        ? 'ready'
        : !data && (isPending || isFetching)
          ? 'loading'
          : isError
            ? 'error'
            : 'ready',
      snippets: data?.snippets ?? EMPTY_SNIPPETS,
      hiddenReferenceIds: data?.hidden_reference_ids,
    })
  }, [data, enabled, isError, isFetching, isPending, source])

  // The NodeView renders in its own React root, outside the query tree, so its
  // "Try again" has to re-run the fetch from here.
  source.retry = () => void refetch()

  return source
}
